var template = require('@/modules/components/playlist/carousel.handlebars')
const shortVideoTemplate = require('@/modules/components/lora/short-video.handlebars')
import NextIcon from '@/assets/media/icons/next-white.svg'
import PrevIcon from '@/assets/media/icons/prev-white.svg'
import { MIN_INDEX, SHORT_VIDEO_TRACKING_TYPE, PAGE_SIZE } from '@/configs/constants'
import { STATUS_RESPONSES, MANUAL_CART_EVENTS, DEFAULT_MIN_HEIGHT_TO_SHOW_BTN } from '@/configs/settings'
import GlobalSettings from '@/core/global-settings'
import { debounce, initialObserver, appendCommonStyling } from '@/helpers'
import { updatePlaylistData } from '~/store/playlist'
import EventManager from '@/core/event-manager'
import { v4 as uuidv4 } from 'uuid'

class LoraCarouselPlaylist extends HTMLElement {
    constructor() {
        super()
        this.attachShadow({ mode: 'open' })

        // core state
        this.eventManager = new EventManager()
        this.httpInstance = GlobalSettings.httpInstance
        this.handlebarTemplate = template
        this.shortVideoTemplate = shortVideoTemplate

        // runtime collections
        this.itemElements = []
        this.videos = []

        // ui handles / icons
        this.storyBlockElement = null
        this.prevIcon = PrevIcon
        this.nextIcon = NextIcon
        this.listInnerElement = null

        // layout / nav
        this.itemWidth = 200
        this.indexCarousel = 0
        this.lastScrollLeft = 0
        this.endScroll = false

        // paging
        this.params = {
            pageSize: PAGE_SIZE,
            pageNum: 1,
            totalPages: 0,
            isEnded: false,
        }

        // misc
        this.clickedNavBtn = false
        this.clickedBtnTimeoutId = null
        this.uuid = uuidv4()
        this.organizationId = null
        this.customTagName = ''
    }

    /* =========================
     * lifecycle
     * =======================*/
    connectedCallback() {
        this.customTagName = this.shadowRoot.host.tagName
        this.getDataAttributes()
        this.fetchData()
    }

    /* =========================
     * attributes / config
     * =======================*/
    getDataAttributes() {
        this.ctaMode = this.getAttribute('cta')
        this.playlistId = this.getAttribute('playlist-id')
        this.enableFullscreen = this.getAttribute('enable-fullscreen') == 'false' ? false : true
        this.gutters = this.getAttribute('gutters') ? parseFloat(this.getAttribute('gutters')) : 10
        this.videoHeight = this.getAttribute('video-height') ? parseFloat(this.getAttribute('video-height')) : 0
        this.itemsPerRow = this.getAttribute('item-per-row') ? parseFloat(this.getAttribute('item-per-row')) : 4.2
        this.itemsPerRowTablet = this.getAttribute('item-per-row-tablet')
            ? parseFloat(this.getAttribute('item-per-row-tablet'))
            : 2.2
        this.itemsPerRowMobile = this.getAttribute('item-per-row-mobile')
            ? parseFloat(this.getAttribute('item-per-row-mobile'))
            : 1.2
        this.positionNav = this.getAttribute('position-nav') ? this.getAttribute('position-nav') : '-60px'
        this.slidesToScroll = this.getAttribute('slides-to-scroll')
            ? parseInt(this.getAttribute('slides-to-scroll'))
            : Math.floor(this.itemsPerRow)
        this.loop = this.getAttribute('loop') !== null && this.getAttribute('loop') !== 'false'
        this.autoScale = this.getAttribute('auto-scale') !== null && this.getAttribute('auto-scale') !== 'false'
        this.pipStyle = this.getAttribute('pip-style') || 'bottom: 0px; right: 15px;'
        this.isForceNewTab = this.hasAttribute('fs-force-new-tab') && this.getAttribute('fs-force-new-tab') !== 'false'
        this.minVideoHeightToShowButtons = this.getAttribute('min-h-btns') || DEFAULT_MIN_HEIGHT_TO_SHOW_BTN
    }

    /* =========================
     * data
     * =======================*/
    async fetchData() {
        if (this.params.isEnded) return
        if (!this.httpInstance.repositories) return

        try {
            const { code, data } = await this.httpInstance.repositories.shortVideo.playlistDetail(
                this.playlistId,
                this.params.pageSize,
                this.params.pageNum
            )

            if (code !== STATUS_RESPONSES.SUCCESS) return

            this.params.totalPages = data.shortVideos.totalPages
            this.params.pageNum++
            this.organizationId = data.organizationId
            if (this.params.pageNum > this.params.totalPages) this.params.isEnded = true

            data.shortVideos = {
                ...data.shortVideos,
                data: data.shortVideos?.data.map((e) => ({
                    ...e,
                    organizationId: data.organizationId,
                    tagName: this.customTagName,
                    pipStyle: this.pipStyle,
                    enableFullscreen: this.enableFullscreen,
                    uuid: this.uuid,
                    minVideoHeightToShowButtons: this.minVideoHeightToShowButtons,
                    enableLikeFeature: true,
                })),
            }

            const previousPlaylistLength = this.videos.length
            this.videos = [...this.videos, ...data.shortVideos.data]

            if (this.params.pageNum <= 2) {
                this.render()
            } else {
                this.renderMore(previousPlaylistLength, data.shortVideos.data)
            }

            updatePlaylistData(this.playlistId, {
                pageSize: this.params.pageSize,
                pageNum: this.params.pageNum - 1,
                isEnded: this.params.isEnded,
                videos: [...this.videos],
                data,
            })

            const elementObserver = this.itemElements[this.videos.length - 1]
            initialObserver(elementObserver, (entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        observer.unobserve(elementObserver)
                        this.fetchData()
                    }
                })
            })
        } catch (e) {
            console.error(e)
        }
    }

    /* =========================
     * rendering
     * =======================*/
    render() {
        if (this.videos.length === 0) return

        const context = {
            playlistId: this.playlistId,
            videos: this.videos,
            ctaMode: this.ctaMode,
            gutters: `${this.gutters}px`,
            itemsPerRow: this.itemsPerRow,
            itemsPerRowTablet: this.itemsPerRowTablet,
            itemsPerRowMobile: this.itemsPerRowMobile,
            positionNav: this.positionNav,
            prevIcon: this.prevIcon,
            nextIcon: this.nextIcon,
            loop: this.loop,
            pipStyle: this.pipStyle,
            uuid: this.uuid,
            organizationId: this.organizationId,
            tagName: this.customTagName,
            enableFullscreen: this.enableFullscreen,
            isForceNewTab: this.isForceNewTab,
            minVideoHeightToShowButtons: this.minVideoHeightToShowButtons,
        }

        this.shadowRoot.innerHTML = `${this.handlebarTemplate(context)}`
        appendCommonStyling(this.shadowRoot)

        this.windowResizeEvent()
        this.calculatorItemLayout()
        this.registerEvents()
        this.handleInternalEvents()
    }

    renderMore(previousPlaylistLength, videos) {
        const innerList = this.shadowRoot.querySelector('.lora-carousel__list-inner')
        if (!innerList) return

        videos.forEach((v, index) => {
            const context = { ...v, videoId: v.id, videoType: v.overlayType }
            const wrapperCarouselItem = document.createElement('div')
            wrapperCarouselItem.classList = 'lora-carousel__item'
            wrapperCarouselItem.setAttribute('data-index', previousPlaylistLength + index)

            const carouselItem = document.createElement('video-embed')
            carouselItem.setAttribute('video-id', v.id)
            carouselItem.setAttribute('data-index', previousPlaylistLength + index)
            carouselItem.setAttribute('generality', '')
            carouselItem.setAttribute('group', '')
            carouselItem.setAttribute('gutters', '')
            carouselItem.setAttribute('simple', '')
            carouselItem.setAttribute('lazyload', '')
            carouselItem.setAttribute('cta', '')
            carouselItem.setAttribute('wrapper', 'lora-carousel-playlist')
            carouselItem.setAttribute('thumbnail', v.thumbnail)
            carouselItem.setAttribute('title', v.title)
            carouselItem.setAttribute('pip-style', v.pipStyle)
            carouselItem.setAttribute('tag-name', v?.tagName)
            carouselItem.setAttribute('organization-id', v?.organizationId)
            carouselItem.setAttribute('uuid', v?.uuid)
            carouselItem.setAttribute('enable-fullscreen', v?.enableFullscreen)
            carouselItem.setAttribute('min-h-btns', v?.minVideoHeightToShowButtons)
            carouselItem.setAttribute('video-type', v?.overlayType)
            carouselItem.innerHTML = `${this.shortVideoTemplate(context)}`

            wrapperCarouselItem.appendChild(carouselItem)
            innerList.appendChild(wrapperCarouselItem)
        })

        this.windowResizeEvent()
        this.calculatorItemLayout(false)
        this.registerEvents()
    }

    /* =========================
     * layout / sizing
     * =======================*/
    windowResizeEvent() {
        window.addEventListener(
            'resize',
            debounce(() => {
                this.calculatorItemLayout()
            }, 300)
        )
    }

    async updateItemsPerRowValue(widthListContainer) {
        if (!this.videoHeight) return

        try {
            // width by 16:9 height
            this.itemWidth = (this.videoHeight * 9) / 16

            if (this.autoScale && widthListContainer <= this.itemWidth) {
                this.itemWidth = widthListContainer
            }

            const itemWidthOuter = this.itemWidth + 2 * this.gutters
            const maxItems = parseFloat((widthListContainer / itemWidthOuter).toFixed(1))

            this.itemsPerRow = maxItems
            this.itemsPerRowTablet = maxItems
            this.itemsPerRowMobile = maxItems
            this.slidesToScroll = maxItems ? Math.floor(maxItems) : 1
        } catch (error) {
            console.error('Error in updateItemsPerRowValue:', error)
        }
    }

    _wrapNeighbors(currentIndex, lengthItems) {
        let prevIndex = currentIndex - 1
        let nextIndex = currentIndex + 1
        if (this.loop) {
            if (prevIndex < 0) prevIndex = lengthItems - 1
            if (nextIndex > lengthItems - 1) nextIndex = 0
        }
        return { prevIndex, nextIndex }
    }

    async calculatorItemLayout(scrollToLeft = true) {
        const listContainer = this.shadowRoot.querySelector('.lora-carousel__list')
        this.listInnerElement = this.shadowRoot.querySelector('.lora-carousel__list-inner')
        this.itemElements = this.shadowRoot.querySelectorAll('.lora-carousel__item')

        const widthListContainer = listContainer.clientWidth

        if (this.videoHeight) {
            await this.updateItemsPerRowValue(widthListContainer)
        } else {
            if (widthListContainer > 1024) {
                this.itemWidth = widthListContainer / this.itemsPerRow - 2 * this.gutters
                this.slidesToScroll = this.itemsPerRow ? Math.floor(this.itemsPerRow) : 1
            } else if (widthListContainer > 768) {
                this.itemWidth = widthListContainer / this.itemsPerRowTablet - 2 * this.gutters
                this.slidesToScroll = this.itemsPerRowTablet ? Math.floor(this.itemsPerRowTablet) : 1
            } else {
                this.itemWidth = widthListContainer / this.itemsPerRowMobile - 2 * this.gutters
                this.slidesToScroll = this.itemsPerRowMobile ? Math.floor(this.itemsPerRowMobile) : 1
            }
            if (widthListContainer <= this.itemWidth) {
                this.itemWidth = widthListContainer
            }
        }

        const itemWidthOuter = this.itemWidth + 2 * this.gutters

        this.itemElements.forEach((itemElement) => {
            itemElement.style.width = `${itemWidthOuter}px`
            const videoEmbed = itemElement.querySelector('video-embed')
            videoEmbed.setAttribute('item-width', this.itemWidth)
        })

        // NOTE: keep original behavior (auto-scroll on layout unless disabled)
        if (scrollToLeft) {
            this.moveSlide(itemWidthOuter, this.indexCarousel)
        }
    }

    /* =========================
     * events (internal)
     * =======================*/
    handleInternalEvents() {
        window.addEventListener('OPEN_FULLSCREEN', (event) => {
            const { playlistId, videoId, uuid } = event?.detail
            if (this.uuid !== uuid) return

            const fullscreenElm = document.querySelector('b-fullscreen')
            this.fullscreenInternalEvents(fullscreenElm)
            this.itemElements.forEach((itemElement) => {
                const videoEmbed = itemElement.querySelector('video-embed')
                videoEmbed.setAttribute('disable-tracking', true)
            })
        })

        window.addEventListener('EXIT_FULLSCREEN', (event) => {
            const { playlistId, videoId, uuid } = event?.detail
            if (this.uuid !== uuid) return

            this.itemElements.forEach((itemElement) => {
                const videoEmbed = itemElement.querySelector('video-embed')
                videoEmbed.setAttribute('disable-tracking', false)
            })
        })
    }

    fullscreenInternalEvents(el) {
        el.eventManager.on(MANUAL_CART_EVENTS.SYNC_PRODUCT, (data, cb) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.SYNC_PRODUCT, data, cb)
        })
        el.eventManager.on(MANUAL_CART_EVENTS.ADD_TO_CART, (data, cb) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.ADD_TO_CART, data, cb)
        })
        el.eventManager.on(MANUAL_CART_EVENTS.UPDATE_ITEM_IN_CART, (data, cb) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.UPDATE_ITEM_IN_CART, data, cb)
        })
        el.eventManager.on(MANUAL_CART_EVENTS.SYNC_CART_STATE, (data, cb) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.SYNC_CART_STATE, data, cb)
        })
        el.eventManager.on(MANUAL_CART_EVENTS.CHECKOUT, (data, cb) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.CHECKOUT, data, cb)
        })
        el.eventManager.on('UPDATE_CURRENT_INDEX', (data) => {
            const itemWidthOuter = this._getItemWidthOuter()
            this.moveSlide(itemWidthOuter, parseInt(data))
        })
    }

    /* =========================
     * events (public wiring)
     * =======================*/
    registerEvents() {
        this._bindScroll()
        this._bindItemClicks()
        this._bindNavHandlers()
    }

    _bindScroll() {
        this.listInnerElement.addEventListener('scroll', this._onScroll)
        this.scrollCarouselFn()
    }

    _bindItemClicks() {
        this.itemElements.forEach((itemElement) => {
            itemElement.onclick = () => this._handleItemClick(itemElement)
        })
    }

    _bindNavHandlers() {
        const { prevNavBtn, nextNavBtn } = this._getNavButtons()
        prevNavBtn.onclick = () => this._onPrevClick()
        nextNavBtn.onclick = (e) => this._onNextClick(e)
    }

    /* =========================
     * scroll / nav behavior
     * =======================*/
    _onScroll = () => {
        this.scrollCarouselFn()
    }

    scrollCarouselFn() {
        this.getScrollPosition()

        const currentScrollLeft = this.listInnerElement.scrollLeft
        this._updateNavVisibility(currentScrollLeft)

        const dir = this._getScrollDirection(currentScrollLeft, this.lastScrollLeft)
        this.lastScrollLeft = currentScrollLeft

        const itemWidthOuter = this._getItemWidthOuter()
        const itemPerScreen = this.itemsPerRowMobile // keep original behavior
        const lengthItems = this.videos.length

        if (!this.loop && this.params.isEnded && this.indexCarousel >= this.videos.length - 1) {
            return
        }

        let currentIndex = this._computeCurrentIndexByDirection({
            dir,
            itemWidthOuter,
            itemPerScreen,
            lengthItems,
        })

        const { prevIndex, nextIndex } = this._wrapNeighbors(currentIndex, lengthItems)

        this.itemElements.forEach((itemElement, index) => {
            this._applyItemState({
                itemElement,
                index,
                currentIndex,
                prevIndex,
                nextIndex,
                isAutoPlay: index === currentIndex,
            })
        })
    }

    getScrollPosition() {
        if (!this.listInnerElement) return
        const totalWidth = Math.round(this.listInnerElement.scrollWidth)
        const visibleWidth = Math.round(this.listInnerElement.getBoundingClientRect().width)
        const currentScrollLeft = Math.round(this.listInnerElement.scrollLeft)
        this.endScroll = currentScrollLeft >= totalWidth - visibleWidth
    }

    delayNavigator() {
        this.clickedNavBtn = true
        if (!this.clickedBtnTimeoutId) {
            this.clickedBtnTimeoutId = setTimeout(() => {
                clearTimeout(this.clickedBtnTimeoutId)
                this.clickedBtnTimeoutId = null
                this.clickedNavBtn = false
            }, 500)
        }
    }

    async _onPrevClick() {
        if (this.clickedNavBtn) return
        this.delayNavigator()

        const itemWidthOuter = this._getItemWidthOuter()
        const lengthItems = this.videos.length
        const listContainer = this._getListContainer()
        const widthListContainer = listContainer.clientWidth

        this.getScrollPosition()
        await this._updateItemsPerRow(widthListContainer)
        const itemPerScreen = this._computeItemPerScreen(widthListContainer)

        let currentPositionIndex = this._computeCurrentPositionIndex({
            mode: 'round',
            itemWidthOuter,
            itemPerScreen,
        })

        let nextIndexCarousel = currentPositionIndex - this.slidesToScroll

        if (this.lastScrollLeft <= 0) {
            nextIndexCarousel = this.loop && this.indexCarousel <= 0 ? lengthItems - this.slidesToScroll : 0
        }
        if (nextIndexCarousel <= 0) nextIndexCarousel = 0

        const { nextNavBtn } = this._getNavButtons()
        nextNavBtn.style.display = 'flex'
        this.moveSlide(itemWidthOuter, nextIndexCarousel)
    }

    async _onNextClick(event) {
        event.stopPropagation()
        if (this.clickedNavBtn) return
        this.delayNavigator()

        const itemWidthOuter = this._getItemWidthOuter()
        const lengthItems = this.videos.length
        const listContainer = this._getListContainer()
        const widthListContainer = listContainer.clientWidth

        this.getScrollPosition()
        await this._updateItemsPerRow(widthListContainer)
        const itemPerScreen = this._computeItemPerScreen(widthListContainer)

        let currentPositionIndex = this._computeCurrentPositionIndex({
            mode: 'floor',
            itemWidthOuter,
            itemPerScreen,
        })

        let nextIndexCarousel = Math.ceil(currentPositionIndex + this.slidesToScroll)

        if (this.endScroll && this.params.isEnded) {
            nextIndexCarousel = this.loop ? 0 : lengthItems - 1
        }
        if (!this.loop && this.params.isEnded && this.indexCarousel >= this.videos.length - 1) return

        const { prevNavBtn } = this._getNavButtons()
        prevNavBtn.style.display = 'flex'
        this.moveSlide(itemWidthOuter, nextIndexCarousel)
    }

    moveSlide(itemWidthOuter, nextIndexCarousel, isAutoPlay = true) {
        this._setScrollPosition(itemWidthOuter, nextIndexCarousel)

        const isNextMove = nextIndexCarousel && this.indexCarousel < nextIndexCarousel ? true : false
        this.indexCarousel = nextIndexCarousel

        const lengthItems = this.videos.length
        const { currentIndex, prevIndex, nextIndex } = this._computeNeighborIndices(
            lengthItems,
            this.indexCarousel,
            isNextMove,
            this.loop
        )

        this.itemElements.forEach((itemElement, index) => {
            this._applyItemState({
                itemElement,
                index,
                currentIndex,
                prevIndex,
                nextIndex,
                isAutoPlay,
            })
        })
    }

    /* =========================
     * helpers (DOM / math)
     * =======================*/
    _getListContainer() {
        return this.shadowRoot.querySelector('.lora-carousel__list')
    }

    _getNavButtons() {
        return {
            prevNavBtn: this.shadowRoot.querySelector('.lora-carousel__nav-prev-btn'),
            nextNavBtn: this.shadowRoot.querySelector('.lora-carousel__nav-next-btn'),
        }
    }

    _computeItemPerScreen(widthListContainer) {
        if (widthListContainer > 1024) return this.itemsPerRow
        if (widthListContainer > 768) return this.itemsPerRowTablet
        return this.itemsPerRowMobile
    }

    _getItemWidthOuter() {
        return this.itemWidth + 2 * this.gutters
    }

    _updateItemsPerRow(widthListContainer) {
        return this.updateItemsPerRowValue(widthListContainer)
    }

    _computeCurrentPositionIndex({ mode, itemWidthOuter, itemPerScreen }) {
        const base = (this.listInnerElement.scrollLeft + itemWidthOuter * itemPerScreen) / itemWidthOuter
        const headIndex = mode === 'round' ? Math.round(base) : Math.floor(base)
        let currentPositionIndex = headIndex - itemPerScreen
        if (currentPositionIndex < 0) currentPositionIndex = 0
        return currentPositionIndex
    }

    _setScrollPosition(itemWidthOuter, nextIndexCarousel) {
        if (nextIndexCarousel <= 0) {
            this.listInnerElement.scrollLeft = 0
        } else {
            const moveStep = itemWidthOuter * nextIndexCarousel
            this.listInnerElement.scrollLeft = moveStep
        }
    }

    _computeNeighborIndices(lengthItems, indexCarousel, isNextMove, loop) {
        let currentIndex = isNextMove ? Math.floor(indexCarousel) : Math.ceil(indexCarousel)
        let prevIndex = currentIndex - 1
        let nextIndex = currentIndex + 1

        if (loop) {
            if (prevIndex < 0) prevIndex = lengthItems - 1
            if (nextIndex > lengthItems - 1) nextIndex = 0
        }

        return { currentIndex, prevIndex, nextIndex }
    }

    _applyItemState({ itemElement, index, currentIndex, prevIndex, nextIndex, isAutoPlay }) {
        const videoComponent = itemElement.querySelector('video-embed')
        const videoElement = videoComponent?.shadowRoot?.querySelector('.lora-video-embed__video')
        const overlayElement = videoComponent?.shadowRoot?.querySelector('.lora-video-embed__overlay')

        videoComponent.setAttribute('muted', this.muted ? 'true' : 'false')

        const isGroup = index === currentIndex || index === nextIndex || index === prevIndex

        if (isGroup) {
            if (isAutoPlay && index === currentIndex) {
                if (videoElement)
                    setTimeout(() => {
                        videoElement.play()
                    }, 500)
                videoComponent.setAttribute('autoplay', '')
                itemElement.classList.add('active')
                videoComponent.removeAttribute('lazyload')
            } else {
                videoComponent.removeAttribute('lazyload')
                videoComponent.removeAttribute('no-action-hover')
                videoComponent.removeAttribute('autoplay')
                itemElement.classList.remove('active')
                if (videoElement) videoElement.pause()
                if (overlayElement) overlayElement.style.display = 'none'
            }

            if (!this.fullscreenMode) videoComponent.setAttribute('loop', '')
            else videoComponent.removeAttribute('loop')

            if (overlayElement) overlayElement.style.display = 'flex'
        } else {
            if (index === nextIndex || index === prevIndex) {
                videoComponent.removeAttribute('lazyload')
                videoComponent.removeAttribute('no-action-hover')
            }
            videoComponent.removeAttribute('autoplay')
            videoComponent.setAttribute('no-action-hover', '')
            itemElement.classList.remove('active')
            if (videoElement) videoElement.pause()
            if (overlayElement) overlayElement.style.display = 'none'
        }
    }

    _getNavEls() {
        return {
            prev: this.shadowRoot.querySelector('.lora-carousel__nav__prev'),
            next: this.shadowRoot.querySelector('.lora-carousel__nav__next'),
        }
    }

    _updateNavVisibility(currentScrollLeft) {
        const { prev, next } = this._getNavEls()

        if (currentScrollLeft <= 0) {
            if (!this.loop) prev.style.display = 'none'
            else prev.style.display = 'flex'
            if (this.itemsPerRow < this.slidesToScroll) next.style.display = 'flex'
            return
        }

        if (currentScrollLeft > 0 && !this.endScroll) {
            prev.style.display = 'flex'
            next.style.display = 'flex'
            return
        }

        if (!this.loop) {
            if (this.params.isEnded) next.style.display = 'none'
        } else {
            next.style.display = 'flex'
        }
    }

    _getScrollDirection(currentLeft, lastLeft) {
        if (currentLeft > lastLeft) return 'forward' // left -> right (Next)
        if (currentLeft < lastLeft) return 'backward' // right -> left (Prev)
        return 'none'
    }

    _computeCurrentIndexByDirection({ dir, itemWidthOuter, itemPerScreen, lengthItems }) {
        const base = (this.listInnerElement.scrollLeft + itemWidthOuter * itemPerScreen) / itemWidthOuter
        const headIndex = dir === 'backward' ? Math.round(base) : Math.floor(base)
        let currentPositionIndex = headIndex - itemPerScreen
        if (currentPositionIndex < 0) currentPositionIndex = 0

        let currentIndex =
            dir === 'backward'
                ? Math.floor(currentPositionIndex + this.slidesToScroll)
                : Math.ceil(currentPositionIndex + this.slidesToScroll)

        if (this.endScroll && this.params.isEnded) {
            currentIndex = this.loop ? 0 : lengthItems - 1
        }
        return currentIndex
    }
}

window.customElements.define('carousel-playlist', LoraCarouselPlaylist)
